/**
 * 3D Gaussian smoothing for SDF volumes.
 *
 * This module provides Gaussian smoothing functionality for signed distance fields,
 * using separable convolution for efficiency (X, Y, Z passes separately).
 */

/**
 * Mapping from smoothing level (1-5) to sigma value.
 * Higher levels produce stronger smoothing.
 */
const SMOOTHING_LEVEL_TO_SIGMA: Record<number, number> = {
    1: 0.2,
    2: 0.4,
    3: 0.7,
    4: 1.0,
    5: 1.4,
};

/**
 * Get the sigma value for a given smoothing level.
 * @param level Smoothing level (1-5)
 * @returns Sigma value for Gaussian kernel
 */
export function getSigmaForLevel(level: number): number {
    const clampedLevel = Math.max(1, Math.min(5, Math.round(level)));
    return SMOOTHING_LEVEL_TO_SIGMA[clampedLevel];
}

/**
 * Apply 3D Gaussian smoothing to an SDF volume in-place.
 * Uses separable convolution (X, Y, Z passes) for efficiency.
 *
 * @param sdf The SDF volume as Float32Array (will be modified in-place)
 * @param nx Number of voxels in X dimension
 * @param ny Number of voxels in Y dimension
 * @param nz Number of voxels in Z dimension
 * @param sigma Standard deviation of Gaussian kernel (in voxels)
 * @param iterations Number of smoothing iterations to apply
 */
export function gaussianSmooth3D(
    sdf: Float32Array,
    nx: number,
    ny: number,
    nz: number,
    sigma: number,
    iterations: number
): void {
    if (sigma <= 0 || iterations <= 0) {
        return;
    }

    // Build 1D Gaussian kernel
    const rad = Math.ceil(3 * sigma);
    const K = new Float32Array(2 * rad + 1);
    let sum = 0;
    for (let i = -rad; i <= rad; i++) {
        const v = Math.exp(-(i * i) / (2 * sigma * sigma));
        K[i + rad] = v;
        sum += v;
    }
    // Normalize kernel
    for (let i = 0; i < K.length; i++) {
        K[i] /= sum;
    }

    const idx = (x: number, y: number, z: number) => x + y * nx + z * nx * ny;
    const tmp = new Float32Array(sdf.length);

    for (let it = 0; it < iterations; it++) {
        // X pass
        for (let z = 0; z < nz; z++) {
            for (let y = 0; y < ny; y++) {
                for (let x = 0; x < nx; x++) {
                    let acc = 0;
                    for (let k = -rad; k <= rad; k++) {
                        const xx = Math.min(nx - 1, Math.max(0, x + k));
                        acc += sdf[idx(xx, y, z)] * K[k + rad];
                    }
                    tmp[idx(x, y, z)] = acc;
                }
            }
        }
        sdf.set(tmp);

        // Y pass
        for (let z = 0; z < nz; z++) {
            for (let y = 0; y < ny; y++) {
                for (let x = 0; x < nx; x++) {
                    let acc = 0;
                    for (let k = -rad; k <= rad; k++) {
                        const yy = Math.min(ny - 1, Math.max(0, y + k));
                        acc += sdf[idx(x, yy, z)] * K[k + rad];
                    }
                    tmp[idx(x, y, z)] = acc;
                }
            }
        }
        sdf.set(tmp);

        // Z pass - optimized to avoid an extra full-volume copy by using
        // reusable 1D buffers per (x, y) column and writing back directly into `sdf`.
        const column = new Float32Array(nz);
        const result = new Float32Array(nz);
        for (let y = 0; y < ny; y++) {
            for (let x = 0; x < nx; x++) {
                // Load the current column along Z into the temporary buffer
                for (let z = 0; z < nz; z++) {
                    column[z] = sdf[idx(x, y, z)];
                }
                // Apply 1D Gaussian convolution along Z using the column buffer
                for (let z = 0; z < nz; z++) {
                    let acc = 0;
                    for (let k = -rad; k <= rad; k++) {
                        const zz = Math.min(nz - 1, Math.max(0, z + k));
                        acc += column[zz] * K[k + rad];
                    }
                    result[z] = acc;
                }
                // Write the smoothed column back into the SDF volume
                for (let z = 0; z < nz; z++) {
                    sdf[idx(x, y, z)] = result[z];
                }
            }
        }
    }
}

/**
 * Apply Gaussian smoothing to Uint8 SDF data and return the smoothed result.
 * Converts to Float32 for processing, then back to Uint8.
 *
 * @param sdfU8 Input SDF as array of Uint8Array slices
 * @param nx Number of voxels in X dimension
 * @param ny Number of voxels in Y dimension
 * @param nz Number of voxels in Z dimension
 * @param threshold The threshold value (typically 127.5)
 * @param falloffMm The falloff distance in mm
 * @param falloffSteps The number of falloff steps
 * @param sigma Gaussian sigma in voxels
 * @param iterations Number of smoothing iterations
 * @returns Smoothed SDF as array of Uint8Array slices
 */
export function gaussianSmooth3DUint8(
    sdfU8: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    falloffMm: number,
    falloffSteps: number,
    sigma: number,
    iterations: number
): Uint8Array[] {
    const N = nx * ny * nz;
    const plane = nx * ny;

    // Convert Uint8 to Float32 (mm scale)
    const u8ToMm = falloffMm / falloffSteps;
    const sdfFloat = new Float32Array(N);
    for (let z = 0; z < nz; z++) {
        for (let i = 0; i < plane; i++) {
            sdfFloat[z * plane + i] = (sdfU8[z][i] - threshold) * u8ToMm;
        }
    }

    // Apply Gaussian smoothing
    gaussianSmooth3D(sdfFloat, nx, ny, nz, sigma, iterations);

    // Convert back to Uint8
    const mmToU8 = falloffSteps / falloffMm;
    const outU8: Uint8Array[] = new Array(nz);
    for (let z = 0; z < nz; z++) {
        outU8[z] = new Uint8Array(plane);
        for (let i = 0; i < plane; i++) {
            const u = threshold + sdfFloat[z * plane + i] * mmToU8;
            outU8[z][i] = u < 0 ? 0 : u > 255 ? 255 : Math.round(u);
        }
    }

    return outU8;
}
